import React, { PropTypes } from "react";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedMessage, intlShape } from "react-intl";
import { connect } from "react-redux";
import { toast } from "react-toastify";

import simpleFetch from "../../../../../../utils/simpleFetch";
import { HIGH_MANAGEMENT } from "../../../../../utils/Roles";
import userHasAccess from "../../../../../utils/userHasAccess";
import { EditButton } from "../../../../common";
import { DataTable } from "../../../../common/DataTable";
import { TERMINATED } from "../../enums/clientStatusTypes";
import ClientGuaranteeEditModal from "./ClientGuaranteeEditModal";

/**
 * Sorts guarantee configurations by guarantee.order, groups dependents under
 * their parent in alphabetical order. Items with order=null come last.
 */
function sortGuaranteesByOrder(configurations, lang) {
  function byName(a, b) {
    const nameA =
      (a.guarantee &&
        a.guarantee.localizations &&
        a.guarantee.localizations[lang] &&
        a.guarantee.localizations[lang].name) ||
      "";
    const nameB =
      (b.guarantee &&
        b.guarantee.localizations &&
        b.guarantee.localizations[lang] &&
        b.guarantee.localizations[lang].name) ||
      "";
    return nameA.localeCompare(nameB);
  }

  const withOrder = configurations
    .filter(
      (c) =>
        c.guarantee &&
        c.guarantee.order !== null &&
        c.guarantee.order !== undefined &&
        c.dependsOnGuaranteeId === null
    )
    .sort((a, b) => a.guarantee.order - b.guarantee.order);

  const withoutOrder = configurations
    .filter(
      (c) =>
        c.guarantee &&
        (c.guarantee.order === null || c.guarantee.order === undefined) &&
        c.dependsOnGuaranteeId === null
    )
    .sort(byName);

  const result = [];

  for (const config of [...withOrder, ...withoutOrder]) {
    result.push({ ...config, indented: false });
    const dependents = configurations
      .filter((c) => c.dependsOnGuaranteeId === config.guaranteeId)
      .sort(byName);
    dependents.forEach((d) => result.push({ ...d, indented: true }));
  }
  return result;
}

class ClientGuaranteesDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      items: null,
      sortedItems: null,
      total: 0,
    };

    this.getData = this.getData.bind(this);
    this.updateClientGuarantee = this.updateClientGuarantee.bind(this);
    this.canEdit = userHasAccess(HIGH_MANAGEMENT);
  }

  getData(offset, rowsPerPage, q) {
    const { clientId, authLanguageCode } = this.props;
    const lang = authLanguageCode || "fr-CA";
    return simpleFetch("GET", `/v2/clients/${clientId}/guarantees`, {})
      .then((data) => {
        const items = data.items || data || [];
        let sorted = sortGuaranteesByOrder(items, lang);

        if (q && q.length >= 3) {
          const normalizeStr = (str) =>
            str
              .normalize("NFD")
              .replace(/[̀-ͯ]/g, "")
              .toLowerCase();
          const normalizedQ = normalizeStr(q);

          sorted = sorted.filter((item) => {
            const name =
              item.guarantee &&
              item.guarantee.localizations &&
              item.guarantee.localizations[lang] &&
              item.guarantee.localizations[lang].name;
            return name && normalizeStr(name).includes(normalizedQ);
          });
        }
        this.setState({ items, sortedItems: sorted, total: sorted.length });
      })
      .catch(() => {
        toast(
          <FormattedMessage id="validation.customMessages.serverErrors.genericError" />,
          { type: toast.TYPE.ERROR, autoClose: false }
        );
      });
  }

  updateClientGuarantee(values) {
    const { clientId } = this.props;
    const {
      clientGuaranteeConfigurationId,
      dependsOnGuaranteeId,
      isAutomaticallyChecked,
    } = values;

    return simpleFetch(
      "PUT",
      `/v2/clients/${clientId}/guarantees/${clientGuaranteeConfigurationId}`,
      {
        body: { dependsOnGuaranteeId, isAutomaticallyChecked },
      }
    )
      .then(() => {
        this.getData();
      })
      .catch((err) => {
        const errorKey = err && err.data && err.data.detail;
        const messageId = errorKey
          ? `validation.customMessages.serverErrors.${errorKey}`
          : "validation.customMessages.serverErrors.genericError";
        toast(
          <FormattedMessage id={messageId} defaultMessage={errorKey} />,
          { type: toast.TYPE.ERROR, autoClose: false }
        );
      });
  }

  getCols() {
    const { authLanguageCode, isArchivedOrTerminated } = this.props;
    const lang = authLanguageCode || "fr-CA";
    const columns = [
      {
        /* No key/sortKeys = sort disabled on this column */
        name: (
          <FormattedMessage id="clientGuarantees.datatable.columns.guarantee" />
        ),
        display: (row) => {
          const name =
            row.guarantee &&
            row.guarantee.localizations &&
            row.guarantee.localizations[lang] &&
            row.guarantee.localizations[lang].name;
          return (
            <span className={row.indented ? "client-guarantee-indented" : ""}>
              {name}
            </span>
          );
        },
      },
      {
        name: (
          <FormattedMessage id="clientGuarantees.datatable.columns.dependsOn" />
        ),
        display: (row) => {
          if (!row.dependsOnGuaranteeId || !this.state.items) {
            return null;
          }
          const parent = this.state.items.find(
            (i) => i.guaranteeId === row.dependsOnGuaranteeId
          );
          const parentName =
            parent &&
            parent.guarantee &&
            parent.guarantee.localizations &&
            parent.guarantee.localizations[lang] &&
            parent.guarantee.localizations[lang].name;
          return <span>{parentName || String(row.dependsOnGuaranteeId)}</span>;
        },
      },
      {
        name: (
          <FormattedMessage id="clientGuarantees.datatable.columns.isAutomaticallyChecked" />
        ),
        display: (row) => (
          <span>
            {row.isAutomaticallyChecked ? (
              <FormattedMessage id="common.yes" />
            ) : (
              <FormattedMessage id="common.no" />
            )}
          </span>
        ),
      },
      ...((this.canEdit && !isArchivedOrTerminated)
        ? [
          {
            name: <FormattedMessage id="datatable.actions" />,
            display: (row) => (
              <ButtonToolbar key={row.clientGuaranteeConfigurationId}>
                <EditButton
                  title={
                    <FormattedMessage id="clientGuarantees.datatable.actions.edit" />
                  }
                  label={<FormattedMessage id="common.edit" />}
                  form={ClientGuaranteeEditModal}
                  data={row}
                  props={{
                    clientGuarantees: this.state.items,
                  }}
                  editFunction={this.updateClientGuarantee}
                />
              </ButtonToolbar>
            ),
          },
        ]
        : []),
    ];
    const widthPct = 100 / columns.length;

    return columns.map((col) => ({
      ...col,
      widthPct,
    }));
  }

  render() {
    const { sortedItems, total } = this.state;

    return (
      <DataTable
        ref="dataTable"
        title={<FormattedMessage id="clientGuarantees.title" />}
        getData={this.getData}
        cols={this.getCols()}
        data={sortedItems}
        offset={0}
        rowsPerPage={sortedItems ? sortedItems.length || 100 : 100}
        totalRows={total}
        sortBy={[]}
        filters={[]}
        q=""
      />
    );
  }
}

ClientGuaranteesDataTable.propTypes = {
  clientId: PropTypes.number,
  authLanguageCode: PropTypes.string,
  isArchivedOrTerminated: PropTypes.bool,
};

ClientGuaranteesDataTable.contextTypes = {
  intl: intlShape,
};

function mapStateToProps(state) {
  const client = state.data.clients.active;
  const isArchivedOrTerminated = client && (client.isArchived || client.clientStatusType == TERMINATED.value)
  return {
    clientId: client.clientId,
    authLanguageCode: state.auth.user.languageCode,
    isArchivedOrTerminated: isArchivedOrTerminated,
  };
}

export default connect(mapStateToProps)(ClientGuaranteesDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/client-guarantees/ClientGuaranteesDataTable.js